import type { GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { Badge } from '@neo/primitives/badge'
import { Flag } from '@neo/primitives/icon'
import type { SelectOption } from '@neo/primitives/select'
import type { RLanguage, RProviders } from 'v2/pages/flow/sidebar/types'
import { getTimeOptions } from 'v2/shared/utils'
import {
  agentSettingsSchema,
  turnDetectorSchema,
  voiceAiConfigSchema,
} from '../../config/agent-settings.validation.config'
import type { AgentSettingsFormValues } from '../../config/base.config'
import { userIdleConfig } from '../../constants'
import {
  getConflictingLanguages,
  isBetaVoiceLanguage,
  isLanguageOptionDisabled,
  isLanguageSelectionAllowed,
  processLanguageSelection,
} from '../../utils/lang-utils'
import { validateVoiceAiConfig } from '../../utils/stt-tts-validator'

// Language option label for the newly supported Sarvam languages. The badge is
// shrink-0 next to a truncating name, so a long language name clips itself
// rather than clipping the Beta tag (which is what a plain inline badge did).
export const LanguageOptionLabel = ({ name, code }: { name: string; code: string }) =>
  isBetaVoiceLanguage(code) ? (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="truncate">{name}</span>
      <Badge variant="tag" className="shrink-0">
        Beta
      </Badge>
    </span>
  ) : (
    name
  )

// A ReactNode label breaks the select's string-based search/title, so searchValue
// carries the plain name on every option.
const buildLanguageOption = (language: RLanguage): SelectOption => ({
  label: <LanguageOptionLabel name={language.name} code={language.code} />,
  value: language.code,
  searchValue: language.name,
  icon: <Flag iso={language.country} circle size={20} />,
})

// Non-blocking warning shown under the Language field when a flow saved before the
// allow-list carries a combination the runtime can no longer transcribe. Save stays
// enabled; the server-side gate is a separate ticket.
const UNSUPPORTED_LANGUAGE_COMBINATION_MESSAGE =
  'This language combination is no longer supported. Existing calls keep working. To change languages, pick a supported combination.'

// Tooltip for a language option the picker has disabled: name the option and the current
// selection it cannot join, so the reason is specific rather than a blanket rule.
// `nameFor` resolves a language code to its display name.
const getLanguageTooltip = (
  name: string,
  code: string,
  selectedLanguages: string[],
  nameFor: (languageCode: string) => string,
): string | undefined => {
  const conflicts = getConflictingLanguages(code, selectedLanguages)
  if (conflicts.length === 0) return undefined

  return `${name} is not supported together with ${conflicts.map(nameFor).join(', ')}`
}

// Helper function to update form fields based on language selection
const updateVoiceAiFields = (
  form: GenericFormType<AgentSettingsFormValues>,
  selectedLanguages: string[],
  providers: RProviders | undefined,
) => {
  const hasEnglish = selectedLanguages.some((lang) => lang === 'en' || lang.startsWith('en-'))

  // Stock idle messages are English prose, so they follow the language selection — but only
  // while untouched; customized text must survive. Timeouts/retries are language-independent
  // and are never reset here.
  const idleConfig = form?.state.values.voice_ai_config?.user_idle_config
  const syncStockIdleMessage = (field: 'reminder_msg' | 'hangup_msg') => {
    const current = idleConfig?.[field]
    if (current && current !== userIdleConfig[field]) return
    form?.setFieldValue(
      `voice_ai_config.user_idle_config.${field}`,
      hasEnglish ? userIdleConfig[field] : '',
    )
  }
  syncStockIdleMessage('reminder_msg')
  syncStockIdleMessage('hangup_msg')

  // Same invariant the app applies on load: keep the selected voice while it's valid for the
  // new language set (custom/cloned voices included), fall back to the language default only
  // when it isn't.
  const validated = validateVoiceAiConfig(form?.state.values.voice_ai_config, providers)
  if (validated?.text_to_speech) {
    form?.setFieldValue('voice_ai_config.text_to_speech', validated.text_to_speech)
  }

  queueMicrotask(() => {
    form?.validateAllFields('change')
  })
}

// Gated by `turnDetectorModelSelectionEnabled` where it is rendered
// (voice-config-tab); the field entry itself is always built.
export const TURN_DETECTOR_MODEL_FIELD = 'voice_ai_config.turn_detector.model'

export const agentSettingsFields = (
  kbOptions: { label: string; value: string }[],
  languages: RLanguage[],
  providers: RProviders | undefined,
  region: string,
  timezones: SelectOption[],
): GenericFieldConfig<AgentSettingsFormValues> => {
  const scheduleAgentSchema = agentSettingsSchema.shape.schedule.unwrap().unwrap()
  return [
    {
      name: 'schedule.timezone',
      componentType: 'select',
      zodValidation: scheduleAgentSchema.shape.timezone,
      inputProps: {
        label: 'Timezone',
        placeholder: 'Select Timezone',
        options: timezones,
      },
    },
    {
      name: 'schedule.start_time',
      componentType: 'select',
      zodValidation: scheduleAgentSchema.shape.start_time,
      className: 'col-span-1 w-full',
      inputProps: {
        label: 'Start Time',
        placeholder: 'Select start time',
        options: getTimeOptions(),
        'data-testid': 'select-start-time',
      },
    },
    {
      name: 'schedule.end_time',
      componentType: 'select',
      onChangeListenTo: ['schedule.start_time'],
      className: 'col-span-1 w-full',
      getDynamicProps: (state) => {
        const currentStartTime = state?.schedule?.start_time
        return {
          inputProps: {
            label: 'End Time',
            placeholder: 'Select end time',
            options: getTimeOptions(false, true),
            'data-testid': 'select-end-time',
          },
          zodVal: currentStartTime
            ? scheduleAgentSchema.shape.end_time.refine((val) => !val || val > currentStartTime, {
                message: 'End time must be after start time',
              })
            : scheduleAgentSchema.shape.end_time,
        }
      },
    },
    {
      name: 'knowledge_base_ids',
      componentType: 'select',
      zodValidation: agentSettingsSchema.shape.knowledge_base_ids,
      inputProps: {
        label: 'Knowledge Base',
        placeholder: 'Select Knowledge Bases',
        multiple: true,
        options: kbOptions,
        'data-testid': 'select-knowledge-base',
      },
    },
    {
      name: 'voice_ai_config.language',
      componentType: 'select',
      zodValidation: voiceAiConfigSchema.shape.language,
      className: 'w-full',
      // Transform array from multi-select to comma-separated string for storage
      // Also process language selection to replace 'en' with region-specific variant and remove duplicates
      eventDataExtractor: (value: string | string[]) => {
        if (!Array.isArray(value)) return String(value ?? '')
        return processLanguageSelection(value, region).join(',')
      },
      valueFormatter: (value: string) => (value ?? '').split(',').filter(Boolean),
      afterOnChange: (_value, _i, form) => {
        const languageValue = form?.state.values.voice_ai_config?.language || ''
        const selectedLanguages = languageValue.split(',').filter(Boolean)
        updateVoiceAiFields(
          form as GenericFormType<AgentSettingsFormValues>,
          selectedLanguages,
          providers,
        )
      },
      getDynamicProps: (state: AgentSettingsFormValues) => {
        const selectedLanguages = state?.voice_ai_config?.language?.split(',').filter(Boolean) || []
        const nameFor = (languageCode: string) =>
          languages.find((language) => language.code === languageCode)?.name ?? languageCode

        return {
          inputProps: {
            inputPlaceholder: 'Search languages',
            size: 'lg',
            options: languages.map((language) => {
              const disabled = isLanguageOptionDisabled(language.code, selectedLanguages)
              return {
                ...buildLanguageOption(language),
                disabled,
                tooltipContent: disabled
                  ? getLanguageTooltip(language.name, language.code, selectedLanguages, nameFor)
                  : undefined,
              }
            }),
            ...(isLanguageSelectionAllowed(selectedLanguages)
              ? {}
              : { helperText: UNSUPPORTED_LANGUAGE_COMBINATION_MESSAGE }),
          },
        }
      },
      inputProps: {
        label: 'Language',
        placeholder: 'Select languages',
        inputPlaceholder: 'Search languages',
        size: 'lg',
        multiple: true,
        options: languages.map(buildLanguageOption),
        'data-testid': 'select-language',
      },
    },
    {
      name: TURN_DETECTOR_MODEL_FIELD,
      componentType: 'select',
      // Optional on purpose: an unsaved value must not make the form invalid and
      // block a publish. The picker shows LiveKit via valueFormatter without
      // writing the key, so absent stays absent until the user chooses.
      zodValidation: turnDetectorSchema.shape.model.optional(),
      className: 'px-0.5',
      valueFormatter: (value: string | undefined) => value || 'livekit',
      inputProps: {
        label: 'Turn detection model',
        helperText: 'Which model decides when the caller has finished speaking.',
        options: [
          { label: 'LiveKit', value: 'livekit' },
          { label: 'Gemma', value: 'gemma' },
        ],
        'data-testid': 'select-turn-detector-model',
      },
    },
  ]
}
